import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { ERROR_MESSAGES } from '../shared/constants/error-message.constants';
import { formatUrl } from '../shared/utils/format-url-util';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { HttpRequestService } from '../shared/services/http-request.service';
import { NotificationService } from '../shared/services/notification.service';
import { UserService } from './user.service';

/** The account holder keeps full access to their own record while frozen. */
const ACCOUNT_HOLDER_ROLES = ['Participant (External)', 'Employer (External)'];

/** Only Compliance may add or remove the freeze. */
const FREEZE_ADMIN_ROLES = ['Compliance'];

/**
 * Contact Details and the Payment Method form are frozen independently. Values must match
 * NextGenCommonConstants.FREEZE_TYPE_* and the freeze_type column.
 */
export type FreezeType = 'CONTACT_DETAILS' | 'PAYMENT_METHOD';

export const FREEZE_TYPE: Record<'CONTACT_DETAILS' | 'PAYMENT_METHOD', FreezeType> = {
  CONTACT_DETAILS: 'CONTACT_DETAILS',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
};

export interface ChangesFreezeState {
  frozen: boolean;
  /** who set the current state, and when; null when this type has never been frozen */
  modifiedByName?: string | null;
  modifiedDate?: string | null;
}

export const UNFROZEN: ChangesFreezeState = { frozen: false };

@Injectable({
  providedIn: 'root',
})
export class ChangesFreezeService {
  constructor(
    private http: HttpRequestService,
    private userService: UserService,
    private notificationService: NotificationService,
    private errorHandler: GlobalErrorHandlerService
  ) {}

  /**
   * The account holder viewing their own record in PPL@Home. The freeze must be invisible to
   * them: no banner, no disabled controls.
   *
   * Note: isInRole asks whether the user HOLDS the role in the selected program, not whether
   * it is the one they are currently acting as -- this branch has no active-role concept.
   */
  isAccountHolder(): boolean {
    return this.userService.isInRole(ACCOUNT_HOLDER_ROLES);
  }

  /** Whether the user may add or remove the freeze. */
  canToggleFreeze(): boolean {
    return this.userService.isInRole(FREEZE_ADMIN_ROLES);
  }

  /**
   * Whether the user may still edit/delete a frozen account. Compliance retains the ability
   * to correct records, and the account holder is never restricted.
   */
  canEditWhileFrozen(): boolean {
    return this.userService.isInRole([...FREEZE_ADMIN_ROLES, ...ACCOUNT_HOLDER_ROLES]);
  }

  /**
   * Whether the active role is blocked from editing right now. The single check a section
   * needs before greying out its Edit/Delete controls.
   */
  isBlocked(state: ChangesFreezeState | undefined | null): boolean {
    return !!state?.frozen && !this.canEditWhileFrozen();
  }

  /**
   * The endpoint returns every freeze type for the account keyed by type; this picks out the
   * one the caller cares about. A type with no rows yet comes back unfrozen.
   */
  getFreezeState(
    programCode: string,
    enrollmentType: string,
    id: number,
    freezeType: FreezeType,
    isAfterUpdate = false
  ): Observable<ChangesFreezeState> {
    const url = formatUrl(ENDPOINTS.GET_CHANGES_FREEZE, {
      programCode,
      enrollmentType,
      id,
    });
    return this.http.get(url, isAfterUpdate).pipe(
      map((response: any) => {
        const data = response?.responsedata?.[freezeType];
        return {
          frozen: !!data?.frozen,
          modifiedByName: data?.modifiedByName ?? null,
          modifiedDate: data?.modifiedDate ?? null,
        };
      }),
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  /**
   * Freezes or unfreezes the account. The backend appends an event row rather than updating one,
   * so no existing record id is needed. Restricted to Compliance server-side.
   */
  setFreeze(
    programCode: string,
    enrollmentType: string,
    id: number,
    freezeType: FreezeType,
    frozen: boolean
  ): Observable<any> {
    const payload = { id, enrollmentType, programCode, freezeType, frozen };
    return this.http.post(ENDPOINTS.SAVE_CHANGES_FREEZE, payload, true).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          frozen ? ERROR_MESSAGES.SUCCESS_CHANGES_FROZEN : ERROR_MESSAGES.SUCCESS_CHANGES_UNFROZEN
        );
      }),
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }
}
